import { cn } from '@/lib/utils';

export const ResourceCardGrid: React.FC<{
  children: React.ReactNode;
  /** `minmax` mínimo de cada card em px (360 cifras/repertórios, 280 artistas). */
  min?: number;
  className?: string;
}> = ({ children, min = 360, className }) => (
  <div
    className={cn('grid gap-4', className)}
    style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${min}px), 1fr))` }}
  >
    {children}
  </div>
);

type ResourceCardProps = {
  /** Tile de ícone ou avatar à esquerda do título. */
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Selo à direita do cabeçalho (tom, status). */
  trailing?: React.ReactNode;
  /** Bloco do meio, com borda superior. */
  meta?: React.ReactNode;
  /** Prévia (ex.: primeiras músicas), abaixo do meta. */
  preview?: React.ReactNode;
  /** Rodapé de ações. */
  actions?: React.ReactNode;
  /** Card apagado (ex.: inativo). */
  muted?: boolean;
  onClick?: () => void;
  className?: string;
};

/** Card das listagens (cifras, repertórios, artistas, usuários). */
export const ResourceCard: React.FC<ResourceCardProps> = ({
  leading,
  title,
  subtitle,
  trailing,
  meta,
  preview,
  actions,
  muted,
  onClick,
  className,
}) => (
  <div
    className={cn(
      'flex flex-col gap-4 rounded-[14px] border border-border bg-card p-4 transition-[transform,border-color,box-shadow] duration-150',
      onClick &&
        'cursor-pointer hover:-translate-y-[3px] hover:border-border-strong hover:shadow-[var(--shadow-float)]',
      muted && 'opacity-60',
      className,
    )}
    onClick={onClick}
  >
    <div className="flex items-center gap-3">
      {leading}
      <div className="min-w-0 flex-1">
        <div className="truncate font-display font-bold text-base text-white leading-tight">
          {title}
        </div>
        {subtitle && (
          <div className="mt-0.5 truncate text-[12.5px] text-muted-foreground">
            {subtitle}
          </div>
        )}
      </div>
      {trailing}
    </div>

    {meta && (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-border border-t pt-3 text-[12.5px] text-muted-foreground">
        {meta}
      </div>
    )}

    {preview}

    {actions && (
      <div
        className="mt-auto flex items-center gap-2 border-border border-t pt-3"
        onClick={(event) => event.stopPropagation()}
      >
        {actions}
      </div>
    )}
  </div>
);

/** Tile 40px de ícone (fundo `accent-surface`). */
export const IconTile: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <span
    className={cn(
      'flex size-10 shrink-0 items-center justify-center rounded-[10px] border border-accent-line bg-accent-surface text-accent-ink',
      className,
    )}
  >
    {children}
  </span>
);

export const InitialsAvatar: React.FC<{
  name: string;
  size?: number;
  className?: string;
}> = ({ name, size = 44, className }) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full border border-border bg-raised-2 font-bold font-display text-[13px] text-white',
        className,
      )}
      style={{ width: size, height: size }}
    >
      {initials}
    </span>
  );
};
